import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog } from "@/components/ui/dialog";
import { useCreateAor } from "@/hooks/use-aors";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

interface CreateAorModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function CreateAorModal({ open, onClose, onCreated }: CreateAorModalProps) {
  const createMut = useCreateAor();
  const [name, setName] = useState("");
  const [contacts, setContacts] = useState<string[]>([""]);
  const [maxContacts, setMaxContacts] = useState(0);

  const addContact = () => setContacts([...contacts, ""]);
  const removeContact = (i: number) => {
    if (contacts.length <= 1) return;
    setContacts(contacts.filter((_, idx) => idx !== i));
  };
  const updateContact = (i: number, val: string) => {
    const next = [...contacts];
    next[i] = val;
    setContacts(next);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    createMut.mutate(
      {
        name: name.trim(),
        contact: contacts.filter((c) => c.trim()),
        max_contacts: maxContacts,
        minimum_expiration: 60,
        maximum_expiration: 7200,
        qualify_frequency: 0,
        qualify_timeout: 3.0,
        qualify_2xx_only: false,
        authenticate_qualify: false,
        remove_existing: false,
        remove_unavailable: false,
        support_path: false,
        outbound_proxy: null,
        mailboxes: null,
      },
      {
        onSuccess: (data) => {
          toast.success("AOR created");
          onCreated(data.id);
          setName("");
          setContacts([""]);
          setMaxContacts(0);
          onClose();
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Create AOR">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Name * <HelpIcon text="AOR identifier. Referenced by endpoints. Example: trunk-aor." /></Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-aor" />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Contact SIP URIs <HelpIcon text="Where to send calls. Each entry is sip:user@host:port. Multiple = parallel forking. Example: sip:provider.com:5060." /></Label>
            <Button type="button" variant="ghost" size="sm" onClick={addContact}>
              <Plus size={12} /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <Input
                  value={c}
                  onChange={(e) => updateContact(i, e.target.value)}
                  placeholder={`sip:${name || "endpoint"}@host`}
                  className="flex-1"
                />
                {contacts.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(i)}>
                    <X size={14} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Max Contacts <HelpIcon text="Maximum simultaneous registrations. 0 = unlimited. Set to 1 for single-device." /></Label>
          <Input
            type="number"
            value={maxContacts}
            onChange={(e) => setMaxContacts(Number(e.target.value))}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={createMut.isPending || !name.trim()}>
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
